
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, Calendar, User } from 'lucide-react';
import { SubstackPost } from '@/services/substackService';

interface SubstackPostCardProps {
  post: SubstackPost;
}

const SubstackPostCard: React.FC<SubstackPostCardProps> = ({ post }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Card className="h-full flex flex-col hover:shadow-lg transition-shadow">
      <CardHeader className="pb-3 flex-shrink-0">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-lg">📰</span>
          <Badge variant="outline" className="text-xs">
            Blog Post
          </Badge>
        </div>
        
        <CardTitle className="text-lg leading-tight font-semibold">
          <div className="line-clamp-2">
            {post.title || 'Substack Article'}
          </div>
        </CardTitle>
        
        {post.author && (
          <p className="text-sm text-muted-foreground truncate">by {post.author}</p>
        )}
      </CardHeader>
      
      <CardContent className="pt-0 flex-1 flex flex-col">
        {post.description && (
          <CardDescription className="flex-1 mb-4">
            <div className="line-clamp-3">
              {post.description}
            </div>
          </CardDescription>
        )}
        
        <div className="flex items-center gap-4 mb-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            <span>{formatDate(post.published_at)}</span>
          </div>
          {post.author && (
            <div className="flex items-center gap-1">
              <User className="h-3 w-3" />
              <span>{post.author}</span>
            </div>
          )}
        </div>
        
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {post.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
            {post.tags.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{post.tags.length - 3}
              </Badge>
            )}
          </div>
        )}
        
        <div className="mt-auto">
          <Button 
            className="w-full" 
            onClick={() => window.open(post.url, '_blank')}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Read Article
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SubstackPostCard;
